import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { serviceOptions } from '../data/services';
import Reveal from '../components/Reveal';
import Button from '../components/Button';

const schema = z.object({
  name: z.string().min(2, 'Name ist erforderlich'),
  contact: z.string().min(4, 'Telefon oder E-Mail ist erforderlich'),
  location: z.string().optional(),
  topic: z.string(),
  message: z.string().optional(),
});

function ContactSection() {
  const [submitState, setSubmitState] = useState({ status: 'idle', error: null });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      topic: 'Kombinierte Anfrage',
    },
  });

  const onSubmit = async (formData) => {
    setSubmitState({ status: 'loading', error: null });
    const isEmail = /@[\w.-]+\.[a-z]{2,}/i.test(formData.contact);
    try {
      const response = await fetch('/api/send-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            service: formData.topic,
            location: formData.location || '',
            phone: isEmail ? '' : formData.contact,
            email: isEmail ? formData.contact : '',
          },
        }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Senden fehlgeschlagen');
      }
      setSubmitState({ status: 'success', error: null });
      reset();
    } catch (err) {
      setSubmitState({ status: 'error', error: err.message || 'E-Mail konnte nicht gesendet werden.' });
    }
  };

  return (
    <section id="kontakt" className="py-12 sm:py-16 md:py-28">
      <div className="container-site mx-auto">
        <Reveal>
          <div className="grid lg:grid-cols-[0.75fr_1fr] gap-8 lg:gap-12 p-6 md:p-12 rounded-card border border-ink/10 bg-white/90 shadow-elevated bg-[radial-gradient(circle_at_0%_0%,rgba(50,185,232,0.12),transparent_18rem)]">
            <div>
              <p className="text-sm font-black uppercase tracking-wider text-signal-dark mb-3">Kontakt</p>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold leading-tight mb-4">Projekt besprechen.</h2>
              <p className="text-ink-soft text-base sm:text-lg leading-relaxed mb-6">
                Direkt anrufen oder eine strukturierte Anfrage senden. Je genauer die Eckdaten, desto schneller lässt
                sich der Einsatz sauber planen.
              </p>
              <ul className="space-y-0">
                <li className="py-3 border-b border-ink/10 font-extrabold">
                  <a href="tel:+436609091992" className="inline-flex items-center gap-2 min-h-11 hover:text-signal-dark transition-colors">
                    <Phone className="w-4 h-4 text-signal" />
                    +43 660 909 1992
                  </a>
                </li>
                <li className="py-3 border-b border-ink/10 font-extrabold">
                  <a href="mailto:office@mh-beton.at" className="inline-flex items-center gap-2 min-h-11 hover:text-signal-dark transition-colors">
                    <Mail className="w-4 h-4 text-signal" />
                    office@mh-beton.at
                  </a>
                </li>
                <li className="py-3 font-extrabold inline-flex items-center gap-2 min-h-11">
                  <MapPin className="w-4 h-4 text-signal" />
                  Hochwaldweg 2A/2/37, 1220 Wien
                </li>
              </ul>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="grid md:grid-cols-2 gap-4">
              <label className="grid gap-1 text-sm font-bold text-ink-soft">
                Name
                <input
                  {...register('name')}
                  type="text"
                  autoComplete="name"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'err-name' : undefined}
                  className="w-full min-h-[46px] px-3 rounded-md border border-steel/20 bg-white/70 text-base text-ink focus:outline focus:outline-2 focus:outline-signal focus:outline-offset-1"
                />
                {errors.name && <span id="err-name" role="alert" className="text-red-500 text-xs">{errors.name.message}</span>}
              </label>

              <label className="grid gap-1 text-sm font-bold text-ink-soft">
                Telefon oder E-Mail
                <input
                  {...register('contact')}
                  type="text"
                  autoComplete="email"
                  aria-invalid={!!errors.contact}
                  aria-describedby={errors.contact ? 'err-contact' : undefined}
                  className="w-full min-h-[46px] px-3 rounded-md border border-steel/20 bg-white/70 text-base text-ink focus:outline focus:outline-2 focus:outline-signal focus:outline-offset-1"
                />
                {errors.contact && <span id="err-contact" role="alert" className="text-red-500 text-xs">{errors.contact.message}</span>}
              </label>

              <label className="grid gap-1 text-sm font-bold text-ink-soft">
                Baustellenort
                <input
                  {...register('location')}
                  type="text"
                  autoComplete="address-level2"
                  placeholder="z. B. 1230 Wien"
                  className="w-full min-h-[46px] px-3 rounded-md border border-steel/20 bg-white/70 text-base text-ink focus:outline focus:outline-2 focus:outline-signal focus:outline-offset-1"
                />
              </label>

              <label className="grid gap-1 text-sm font-bold text-ink-soft">
                Projekt
                <select
                  {...register('topic')}
                  className="w-full min-h-[46px] px-3 rounded-md border border-steel/20 bg-white/70 text-base text-ink focus:outline focus:outline-2 focus:outline-signal focus:outline-offset-1"
                >
                  {serviceOptions.map((option) => (
                    <option key={option.value} value={option.label}>{option.label}</option>
                  ))}
                  <option value="Kombinierte Anfrage">Kombinierte Anfrage</option>
                </select>
              </label>

              <label className="grid gap-1 text-sm font-bold text-ink-soft md:col-span-2">
                Nachricht
                <textarea
                  {...register('message')}
                  rows={5}
                  placeholder="Menge, Termin, Zufahrt, Pumpweg, Besonderheiten"
                  className="w-full px-3 py-2 rounded-md border border-steel/20 bg-white/70 text-base text-ink resize-y focus:outline focus:outline-2 focus:outline-signal focus:outline-offset-1"
                />
              </label>

              <div className="md:col-span-2">
                <Button type="submit" variant="primary" className="w-full md:w-auto" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><span className="w-4 h-4 border-2 border-ink/30 border-t-ink rounded-full animate-spin" /> Wird gesendet…</>
                  ) : (
                    <><Send className="w-[18px] h-[18px]" /> Anfrage senden</>
                  )}
                </Button>
                {submitState.status === 'success' && (
                  <p className="mt-3 flex items-center gap-2 text-teal font-bold text-sm" role="status">
                    <CheckCircle className="w-4 h-4" />
                    Anfrage erfolgreich gesendet. MH-BETON meldet sich so schnell wie möglich.
                  </p>
                )}
                {submitState.status === 'error' && (
                  <p className="mt-3 flex items-center gap-2 text-red-500 font-bold text-sm" role="alert">
                    <AlertCircle className="w-4 h-4" />
                    {submitState.error} Du kannst uns auch direkt anrufen: +43 660 909 1992
                  </p>
                )}
              </div>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default ContactSection;
